import { useSearchParams } from "react-router-dom"

import {
  isPricingSupersededError,
  PRICING_SUPERSEDED_MESSAGE,
} from "~/api/battery-pricing-errors"
import type {
  BatteryContractBlockerCode,
  BatteryContractReadinessDto,
} from "~/api/dto/battery-contract.dto"
import useBatteryContractReadiness from "~/api/hooks/useBatteryContractReadiness"
import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useCalculateBatteryProposalPricing from "~/api/hooks/useCalculateBatteryProposalPricing"
import useSendBatteryContract from "~/api/hooks/useSendBatteryContract"

import { ConcertBatteryStatusDrawer } from "./ConcertBatteryStatusDrawer"
import {
  ContractFixCommand,
  ContractFixLink,
  ContractFixStepLink,
} from "../../ProposalSetup/contract/contract-fix-actions"
import type {
  ContractBlockerFixProps,
  ContractFixStep,
  ContractKindBundle,
} from "../../ProposalSetup/contract/contract-kind-bundle"
import { batteryDrawerReturnTo } from "../financing-status/battery-drawer-launch"
import { CONCERT_BATTERY_FINANCE_TYPE } from "../financing-status/concert/concert-battery-copy"
import {
  PricingOutOfDateIndicator,
  REFRESH_PRICING_BUSY_LABEL,
  REFRESH_PRICING_ERROR_PROMPT,
  REFRESH_PRICING_LABEL,
} from "../pricing-out-of-date"
import {
  batteryProposalPath,
  batterySetupStepPath,
  SETUP_STEP_LABELS,
} from "../setup/setup-steps"
import type { BatterySetupStep } from "../setup/setup-steps"

type BatteryFixProps = ContractBlockerFixProps<BatteryContractReadinessDto>

/** Setup step that clears a blocker, when the seller can act on it in Vision. */
const FIX_STEP: Partial<Record<BatteryContractBlockerCode, BatterySetupStep>> = {
  PRICING_NOT_CALCULATED: `system-pricing`,
  PRICING_OUT_OF_DATE: `system-pricing`,
  FINANCE_PLAN_MISSING: `equipment`,
  FINANCE_TYPE_UNSUPPORTED: `equipment`,
  FINANCING_TERMS_UNAVAILABLE: `finance-product`,
}

/** Steps the fix link names differently from the Setup rail. */
const FIX_STEP_LABEL: Partial<Record<BatterySetupStep, string>> = {
  equipment: `Open Finance Partner & Equipment`,
}

const batteryFixStep = (
  proposalId: string,
  code: BatteryContractBlockerCode,
): ContractFixStep | null => {
  const step = FIX_STEP[code]
  return step
    ? {
        path: batterySetupStepPath(proposalId, step),
        label: FIX_STEP_LABEL[step] ?? `Open ${SETUP_STEP_LABELS[step]}`,
      }
    : null
}

const RefreshPricingFix = ({
  proposalId,
  readiness,
}: BatteryFixProps): JSX.Element | null => {
  // Its success and failure both invalidate contract readiness.
  const refreshPricing = useCalculateBatteryProposalPricing()
  if (!readiness.actions.canRefreshPricing) {
    return null
  }
  return (
    <ContractFixCommand
      label={REFRESH_PRICING_LABEL}
      busyLabel={REFRESH_PRICING_BUSY_LABEL}
      busy={refreshPricing.isLoading}
      errorMessage={
        refreshPricing.isError
          ? isPricingSupersededError(refreshPricing.error)
            ? PRICING_SUPERSEDED_MESSAGE
            : REFRESH_PRICING_ERROR_PROMPT
          : null
      }
      onRun={() => refreshPricing.mutate({ proposalId })}
    />
  )
}

/**
 * A Concert plan's financed payment lives in its application, so the fix is
 * the Concert application status; every other plan picks a Loan Product.
 */
const FinancingTermsFix = ({
  proposalId,
  readiness,
}: BatteryFixProps): JSX.Element | null => {
  const proposal = useBatteryProposal(proposalId).data
  if (proposal?.financePlan?.financeType === CONCERT_BATTERY_FINANCE_TYPE) {
    return (
      <ContractFixLink
        to={`${batteryProposalPath(proposalId)}/contract`}
        label="Open Concert application status"
      />
    )
  }
  const step = batteryFixStep(proposalId, `FINANCING_TERMS_UNAVAILABLE`)
  return step && readiness.actions.viewerCanUpdate ? (
    <ContractFixStepLink step={step} />
  ) : null
}

/** English only: Battery contract templates have no other language. */
const BatteryContractLanguage = (): JSX.Element => <>English</>

/**
 * Contract → Status is one entry; the Finance Plan decides what it shows. A
 * Concert plan shows its application status unless the seller asked for the
 * Powur installation contract.
 */
const useBatteryPartnerOverride = (
  proposalId: string | undefined,
): typeof ConcertBatteryStatusDrawer | null => {
  const [searchParams] = useSearchParams()
  const proposal = useBatteryProposal(proposalId).data
  return proposal?.financePlan?.financeType === CONCERT_BATTERY_FINANCE_TYPE &&
    searchParams.get(`package`) !== `installation`
    ? ConcertBatteryStatusDrawer
    : null
}

/** Battery-Only Proposal reads, commands and fixes for the shared Contract drawer. */
export const batteryContractBundle: ContractKindBundle<BatteryContractReadinessDto> =
  {
    flavorLabel: {
      standard: `Standard Battery contract`,
      participate: `Participate Battery contract`,
    },
    proposalPath: batteryProposalPath,
    returnTo: batteryDrawerReturnTo,
    useReadiness: useBatteryContractReadiness,
    useSend: useSendBatteryContract,
    inlineFixes: {
      PRICING_OUT_OF_DATE: {
        available: (readiness) => readiness.actions.canRefreshPricing,
        Action: RefreshPricingFix,
      },
      FINANCING_TERMS_UNAVAILABLE: {
        available: (readiness) => readiness.actions.viewerCanUpdate,
        Action: FinancingTermsFix,
      },
    },
    fixStep: batteryFixStep,
    PricingOutOfDateIndicator,
    LanguageField: BatteryContractLanguage,
    usePartnerOverride: useBatteryPartnerOverride,
  }
